import { useState, useEffect } from 'react';
import api from '../services/api';

export default function Messages() {
  const [companies, setCompanies] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [messageHistory, setMessageHistory] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [activeTab, setActiveTab] = useState('send');
  const [sending, setSending] = useState(false);
  
  const [form, setForm] = useState({
    company_id: '',
    template_id: '',
    recipient_phone: '',
    recipient_name: '',
    custom_message: ''
  });
  
  const [preview, setPreview] = useState('');

  useEffect(() => {
    fetchCompaniesAndTemplates();
  }, []);

  const fetchCompaniesAndTemplates = async () => {
    try {
      setLoading(true);
      const companiesRes = await api.get('/companies');
      const templatesRes = await api.get('/templates');
      
      setCompanies(Array.isArray(companiesRes.data) ? companiesRes.data : []);
      setTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : []);
      setError('');
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessageHistory = async (companyId) => {
    if (!companyId) return;
    try {
      setHistoryLoading(true);
      const response = await api.get(`/messages/history/${companyId}`);
      setMessageHistory(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError('Failed to fetch message history');
      console.error(err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handlePreview = () => {
    if (form.template_id) {
      const template = templates.find(t => t.id === parseInt(form.template_id));
      if (template) {
        setPreview(template.content);
      }
    } else if (form.custom_message) {
      setPreview(form.custom_message);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    setSuccess('');

    try {
      if (!form.company_id || !form.recipient_phone) {
        setError('Company and phone number required');
        setSending(false);
        return;
      }

      const message = form.template_id 
        ? templates.find(t => t.id === parseInt(form.template_id))?.content 
        : form.custom_message;

      if (!message) {
        setError('Please select template or enter custom message');
        setSending(false);
        return;
      }

      const payload = {
        company_id: parseInt(form.company_id),
        template_id: form.template_id ? parseInt(form.template_id) : null,
        phone: form.recipient_phone,
        recipient_name: form.recipient_name || 'Unknown',
        message: message,
        sent_by_user_id: 4 // admin user id
      };

      const response = await api.post('/messages/send', payload);
      
      setSuccess('Message sent successfully!');
      setTimeout(() => setSuccess(''), 3000);
      
      setForm({
        company_id: form.company_id,
        template_id: '',
        recipient_phone: '',
        recipient_name: '',
        custom_message: ''
      });
      setPreview('');
      
      fetchMessageHistory(form.company_id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send message');
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const handleCompanyChange = (companyId) => {
    setForm({ ...form, company_id: companyId });
    if (companyId) {
      fetchMessageHistory(companyId);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ margin: '0 0 20px 0' }}>Messages</h2>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '2px solid #e0e0e0', paddingBottom: '10px' }}>
        <button
          onClick={() => setActiveTab('send')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'send' ? '#0066cc' : '#f0f0f0',
            color: activeTab === 'send' ? 'white' : '#333',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: activeTab === 'send' ? 'bold' : 'normal'
          }}
        >
          📤 Send Message (Manual)
        </button>
        <button
          onClick={() => setActiveTab('history')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'history' ? '#0066cc' : '#f0f0f0',
            color: activeTab === 'history' ? 'white' : '#333',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: activeTab === 'history' ? 'bold' : 'normal'
          }}
        >
          📋 Message History
        </button>
        <button
          onClick={() => setActiveTab('automation')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'automation' ? '#0066cc' : '#f0f0f0',
            color: activeTab === 'automation' ? 'white' : '#333',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: activeTab === 'automation' ? 'bold' : 'normal'
          }}
        >
          ⚙️ Automation
        </button>
      </div>

      {error && (
        <div style={{ 
          padding: '12px', 
          backgroundColor: '#fee', 
          color: '#c00', 
          borderRadius: '4px', 
          marginBottom: '20px',
          border: '1px solid #fcc'
        }}>
          ❌ {error}
        </div>
      )}

      {success && (
        <div style={{ 
          padding: '12px', 
          backgroundColor: '#e8f5e9', 
          color: '#2e7d32', 
          borderRadius: '4px', 
          marginBottom: '20px',
          border: '1px solid #4caf50'
        }}>
          ✅ {success}
        </div>
      )}

      {activeTab === 'send' && (
        <div>
          <form onSubmit={handleSend} style={{ 
            padding: '20px', 
            border: '1px solid #ddd', 
            borderRadius: '4px',
            backgroundColor: '#f9f9f9',
            marginBottom: '20px'
          }}>
            <h3 style={{ marginTop: 0 }}>Send Message Manually</h3>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Select Company *</label>
              <select
                value={form.company_id}
                onChange={(e) => handleCompanyChange(e.target.value)}
                required
                style={{ 
                  width: '100%', 
                  padding: '10px', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  boxSizing: 'border-box',
                  fontSize: '14px'
                }}
              >
                <option value="">-- Select Company --</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name} ({c.gst_number})</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Recipient Phone *</label>
              <input
                type="tel"
                placeholder="+91-9876543210 or 9876543210"
                value={form.recipient_phone}
                onChange={(e) => setForm({ ...form, recipient_phone: e.target.value })}
                required
                style={{ 
                  width: '100%', 
                  padding: '10px', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  boxSizing: 'border-box',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Recipient Name</label>
              <input
                type="text"
                placeholder="Optional"
                value={form.recipient_name}
                onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
                style={{ 
                  width: '100%', 
                  padding: '10px', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  boxSizing: 'border-box',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Select Template OR Custom Message</label>
              <select
                value={form.template_id}
                onChange={(e) => setForm({ ...form, template_id: e.target.value, custom_message: '' })}
                style={{ 
                  width: '100%', 
                  padding: '10px', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  boxSizing: 'border-box',
                  fontSize: '14px',
                  marginBottom: '10px'
                }}
              >
                <option value="">-- No Template --</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.name} ({t.category})</option>
                ))}
              </select>

              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Or Custom Message</label>
              <textarea
                placeholder="Enter custom message here"
                value={form.custom_message}
                onChange={(e) => setForm({ ...form, custom_message: e.target.value, template_id: '' })}
                style={{ 
                  width: '100%', 
                  padding: '10px', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  boxSizing: 'border-box',
                  fontSize: '14px',
                  minHeight: '80px',
                  fontFamily: 'monospace'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
              <button 
                type="button"
                onClick={handlePreview}
                style={{ 
                  padding: '10px 20px', 
                  backgroundColor: '#ff9800', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Preview
              </button>
              <button 
                type="submit"
                disabled={sending}
                style={{ 
                  padding: '10px 20px', 
                  backgroundColor: '#0066cc', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: sending ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  opacity: sending ? 0.6 : 1
                }}
              >
                {sending ? 'Sending...' : '📤 Send Now'}
              </button>
            </div>

            {preview && (
              <div style={{ 
                padding: '15px', 
                backgroundColor: '#e3f2fd', 
                border: '1px solid #90caf9',
                borderRadius: '4px',
                marginTop: '15px'
              }}>
                <h4 style={{ marginTop: 0 }}>Preview:</h4>
                <p style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '13px' }}>{preview}</p>
              </div>
            )}
          </form>
        </div>
      )}

      {activeTab === 'history' && (
        <div>
          <h3>Message History</h3>
          {messageHistory.length === 0 ? (
            <p style={{ color: '#999', textAlign: 'center' }}>Select a company above to view message history</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px', fontSize: '12px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f0f0f0', borderBottom: '2px solid #ddd' }}>
                  <th style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'left' }}>Date/Time</th>
                  <th style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'left' }}>Recipient</th>
                  <th style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'left' }}>Phone</th>
                  <th style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'left' }}>Message</th>
                  <th style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'left' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {historyLoading ? (
                  <tr><td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>Loading...</td></tr>
                ) : (
                  messageHistory.map((msg) => (
                    <tr key={msg.id} style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ border: '1px solid #ddd', padding: '10px' }}>
                        {new Date(msg.sent_at).toLocaleString()}
                      </td>
                      <td style={{ border: '1px solid #ddd', padding: '10px' }}>{msg.recipient_name}</td>
                      <td style={{ border: '1px solid #ddd', padding: '10px' }}>{msg.recipient_phone}</td>
                      <td style={{ border: '1px solid #ddd', padding: '10px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {msg.message}
                      </td>
                      <td style={{ border: '1px solid #ddd', padding: '10px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: msg.delivery_status === 'delivered' ? '#e8f5e9' : msg.delivery_status === 'failed' ? '#ffebee' : '#fff3e0',
                          color: msg.delivery_status === 'delivered' ? '#2e7d32' : msg.delivery_status === 'failed' ? '#c62828' : '#e65100',
                          fontSize: '11px'
                        }}>
                          {msg.delivery_status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'automation' && (
        <div style={{ 
          padding: '20px', 
          border: '1px solid #ddd', 
          borderRadius: '4px',
          backgroundColor: '#f9f9f9'
        }}>
          <h3 style={{ marginTop: 0 }}>Automation Settings</h3>

          <div style={{ marginBottom: '20px' }}>
            <h4>⚙️ Daily Compliance Reminders</h4>
            <p><strong>Status:</strong> <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>✅ Active</span></p>
            <p><strong>Schedule:</strong> Every day at 8:00 AM IST</p>
            <p><strong>What it does:</strong></p>
            <ul style={{ margin: '10px 0' }}>
              <li>Checks all companies with auto_send_enabled = true</li>
              <li>Scans compliance items due in next 7 days</li>
              <li>Sends WhatsApp reminder to company contact</li>
              <li>Uses GST Reminder template by default</li>
              <li>Logs all sent messages in message history</li>
            </ul>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <h4>📋 Enable/Disable Auto-Send</h4>
            <p>Go to <strong>Companies</strong> tab → click <strong>Edit</strong> on any company → toggle <strong>"Enable Auto-Send"</strong></p>
          </div>

          <div style={{ 
            padding: '15px', 
            backgroundColor: '#e3f2fd', 
            border: '1px solid #90caf9',
            borderRadius: '4px'
          }}>
            <strong>📌 Note:</strong> Automation runs server-side (Node.js cron job). You can manually send messages anytime using the "Send Message" tab above.
          </div>
        </div>
      )}
    </div>
  );
}
